import { map } from 'lodash';
import { Fragment, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery, UseQueryResult } from 'react-query';

import { usersApi } from 'api';
import {
  CopyText,
  DataGridColumnDefinition,
  Money,
  TronscanLink,
  TronTransactionStatusLabel,
  dataGridColumns,
  CopyTextId,
  CrudTableActionType,
  CrudPage,
} from 'components';
import { QueryKey } from 'enums';
import { CreateUserWalletWrapper } from 'features/wallets';
import { TranslationNamespace } from 'i18n';
import { InternalWalletWithdrawalTransaction, PaginatedData } from 'types';
import { formatUtils, transactionsUtils } from 'utils';

import { WithdrawalTransactionDetailsModal } from '../WithdrawalTransactionDetailsModal';

type Props = {
  queryResult: UseQueryResult<
    PaginatedData<InternalWalletWithdrawalTransaction>
  >;
  withActions?: boolean;
};

export const WithdrawalTransactions: React.FC<Props> = ({
  queryResult,
  withActions,
}) => {
  const { t } = useTranslation(TranslationNamespace.Admin, {
    keyPrefix: 'pages.internal_wallets_transactions',
  });

  const { data: users = [] } = useQuery(
    QueryKey.Users,
    usersApi.getAllWithPlatform,
  );

  const columns = useMemo(
    (): DataGridColumnDefinition<InternalWalletWithdrawalTransaction>[] => [
      dataGridColumns.getIdColumn(),
      {
        header: t('fields.funds_request'),
        valueGetter: (item) => (
          <CopyTextId id={item.fundsRequestId} disableIconColor />
        ),
      },
      {
        header: t('fields.user_wallet'),
        valueGetter: (item) => (
          <div key={item.id}>
            <div>{item.fundsRequest?.user?.name}</div>
            <CreateUserWalletWrapper
              data={{
                userId: item.fundsRequest?.user?.id!,
                address: item.fundsRequest?.recipient,
                network: item.fundsRequest?.network,
              }}
            >
              <CopyText truncate text={item.fundsRequest?.recipient || ''} />
            </CreateUserWalletWrapper>
          </div>
        ),
      },
      {
        header: t('fields.transaction'),
        valueGetter: (item) => <TronscanLink hash={item.fundsRequest.hash} />,
      },
      {
        header: t('fields.amount'),
        valueGetter: (item) => (
          <Money
            value={item.fundsRequest?.amount}
            assetCurrencyId={item.fundsRequest?.asset?.assetCurrencyId}
            symbol
          />
        ),
      },
      {
        header: t('fields.platform_wallet'),
        valueGetter: (item) =>
          item.platformInternalWallet?.address && (
            <CopyText truncate text={item.platformInternalWallet.address} />
          ),
      },
      {
        header: t('fields.energy_requests'),
        valueGetter: (item) => (
          <Fragment>
            {map(item.energyRequests, (request, i) => (
              <div
                key={i}
                className={
                  request.status ? 'tw-text-green-700' : 'tw-text-red-500'
                }
              >
                {request.amount}
              </div>
            ))}
          </Fragment>
        ),
      },
      {
        header: t('fields.status'),
        valueGetter: (item) => (
          <Fragment>
            <TronTransactionStatusLabel status={item.status} />
            {item.retryAfterDate && (
              <div className="tw-text-xs tw-text-neutral-400">
                <div>{`${t('fields.delayed')}`}</div>
                <div>{formatUtils.formatDate(item.retryAfterDate)}</div>
              </div>
            )}
          </Fragment>
        ),
      },
    ],
    [t],
  );

  const filtersDefinitions = useMemo(
    () => transactionsUtils.getCommonFilters(users),
    [users],
  );

  return (
    <CrudPage
      filters={{ filtersDefinitions }}
      table={{
        columns,
        queryResult,
        paginated: true,
        actions: [
          {
            type: CrudTableActionType.Details,
            renderDialog: WithdrawalTransactionDetailsModal,
          },
        ],
        hideActions: !withActions,
      }}
    />
  );
};
